import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import api from '../lib/api';

// Types based on the backend API
export interface Activity {
  id: string;
  type: ActivityType;
  reference?: string;
  description?: string;
  status: string;
  priority: string;
  completedAt?: string | null;
  notes?: string | null;
  conversationId: string;
  channel?: {
    type: string;
    connectionId: string;
  };
  items?: ActivityItem[];
  organizationId: string;
  tenantId: string;
  integrationId?: string;
  sourceId?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  meta?: Record<string, unknown>;
}

export interface ActivityItem {
  id: string;
  type: string;
  title: string;
  filePath: string;
  lineRange: [number, number];
  detectionId: string;
  repositoryId: string;
  integrationId: string;
  severityLevel: string;
  organizationId: string;
}

export type ActivityType = string; // The backend accepts array of strings for filtering

export interface ListActivitiesParams {
  sourceId?: string;
  type?: ActivityType[];
  status?: string[];
  createdAt?: {
    start?: string;
    end?: string;
  };
  limit?: number;
  offset?: number;
  sortBy?: 'createdAt';
  sortOrder?: 'asc' | 'desc';
}

export interface ListActivitiesResponse {
  data: Activity[];
  meta: {
    total: number;
    limit: number;
    offset?: number;
  };
}

// Query keys
export const activityKeys = {
  all: ['activities'] as const,
  lists: (params?: ListActivitiesParams) =>
    [...activityKeys.all, 'list', params] as const,
};

// Hooks
export const useListActivities = (
  params: ListActivitiesParams,
  options?: UseQueryOptions<ListActivitiesResponse, Error>,
) => {
  return useQuery({
    queryKey: activityKeys.lists(params),
    queryFn: async () => {
      const { data } = await api.post<ListActivitiesResponse>(
        '/api/core/pleri/activities',
        params,
      );
      return data;
    },
    enabled: true,
    refetchOnMount: true,
    ...options,
  });
};
